import React, { FC, useRef } from 'react';
import {
  MonoFontLabel,
  OpenSansParagraph,
  RecoletaHeading,
} from '../common/typography';
import { markdownText } from '../../pages/doctors';
import Slider, { Settings } from 'react-slick';

import SliderArrow from '../common/slider/SliderArrow';
import { css } from '@emotion/core';
import theme from '../../styles/theme';
import styled from '@emotion/styled';
import presets from '../../styles/presets';
import Container from '../Homepage/Container';
import { useIntl } from 'react-intl';

export interface ProgramCardProps {
  src: string;
  prefix: string;
  title: string;
  description: string;
}

const MobileArrows = styled.div(css`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  align-self: center;
  margin-left: 20px;

  ${presets.lg} {
    display: none;
  }
`);

const ProgramCard: FC<ProgramCardProps> = ({
  src,
  prefix,
  title,
  description,
}) => {
  return (
    <Container
      css={{
        maxWidth: 400,
      }}
    >
      <img
        src={src}
        alt={title}
        css={{
          width: '100%',
          height: 'auto',
          objectFit: 'contain',
          marginBottom: 20,
        }}
      />
      <MonoFontLabel
        css={{
          fontWeight: 700,
          letterSpacing: '2px',
          lineHeight: '18px',
          color: '#00000080',
          marginBottom: 5,
        }}
      >
        {prefix}
      </MonoFontLabel>
      <RecoletaHeading
        css={{
          fontSize: '24px',
          fontWeight: 600,
          maxWidth: 400, // will be different per section
        }}
      >
        {title}
      </RecoletaHeading>
      <OpenSansParagraph
        css={{
          fontSize: '16px',
          fontWeight: 400,
          lineHeight: '25px',
          marginTop: '1rem',
        }}
      >
        {markdownText(description)}
      </OpenSansParagraph>
    </Container>
  );
};

interface Props {
  dataObj: ProgramCardProps[];
}

const Programs: FC<Props> = ({ dataObj }) => {
  const ref = useRef<Slider>(null);
  const intl = useIntl();

  const sliderSettings: Settings = {
    dots: false,
    infinite: true,
    autoplay: false,
    centerPadding: '0px',
    speed: 500,
    slidesToShow: 2,
    slidesToScroll: 2,
    nextArrow: <SliderArrow position="right" />,
    prevArrow: <SliderArrow position="left" />,
    responsive: [
      {
        breakpoint: theme.breakpoints.lg,
        settings: {
          arrows: false,
          slidesToShow: 1,
          slidesToScroll: 1,
          draggable: true,
        },
      },
    ],
  };

  const settingsStyle = css`
    .slick-next,
    .slick-prev {
      margin: -60px;
    }

    .slick-next:before,
    .slick-prev:before {
      content: '';
    }
  `;

  return (
    <Container
      css={{
        paddingLeft: 0,
        paddingRight: 0,
        maxWidth: 860,
        marginTop: '5rem',
        '@media (max-width: 768px)': {
          marginTop: '3rem',
        },
      }}
    >
      <div
        css={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <RecoletaHeading
          css={{
            fontSize: '32px',
            fontWeight: 500,
            lineHeight: '38px',
            textAlign: 'center',
          }}
        >
          {markdownText(
            intl.formatMessage({
              id: "product.page.programs.title",
              defaultMessage:
                "Der <b style='color: #2AAB3C'>Ablauf</b> deines <br/> 12-Wochen-Programms",
            })
          )}
        </RecoletaHeading>
        <MobileArrows>
          <SliderArrow
            position="left"
            onClick={() => ref.current.slickPrev()}
          />
          <SliderArrow
            position="right"
            onClick={() => ref.current.slickNext()}
          />
        </MobileArrows>
      </div>

      <Slider
        ref={ref}
        {...sliderSettings}
        css={[
          settingsStyle,
          {
            cursor: 'grab',
            marginTop: '1rem',
          },
        ]}
      >
        {dataObj.map((data, index) => (
          <ProgramCard
            key={index}
            src={data.src}
            prefix={data.prefix}
            title={data.title}
            description={data.description}
          />
        ))}
      </Slider>
    </Container>
  );
};

export default Programs;
